import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Progress,
  VStack,
  Divider,
  Input,
  Checkbox,
  IconButton,
  ButtonGroup,
  InputGroup,
  InputRightElement,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";
import { useToast } from "@chakra-ui/react";
import { FaMinus, FaTrash, FaDownload } from "react-icons/fa";
import { AddIcon } from "@chakra-ui/icons";
import { useBreakpointValue } from "@chakra-ui/react";

import axiosInstance from "../../helpers/axiosInstance";
import Translate from "../../components/LanguageTranslation/Translate";

const PackingListDetails = ({
  tripName,
  packingLists,
  setPackingLists,
  selectedListId,
  handleDeletePackingList,
  isBaseScreen,
}) => {
  const [selectedPackingListCategory, setSelectedPackingListCategory] =
    useState(""); // New state for packing list name
  const [selectedPackingListName, setSelectedPackingListName] = useState(""); // New state for packing list name
  const [tripCompanion, setTripCompanion] = useState("");
  const [listItems, setListItems] = useState([]);
  const [newItemName, setNewItemName] = useState("");
  const [hoveredItemKey, setHoveredItemKey] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const cancelRef = React.useRef();
  const isBaseScreenValue = useBreakpointValue({ base: true, md: false });

  const toast = useToast();

  const fetchPackingListById = async () => {
    if (selectedListId) {
      try {
        const response = await axiosInstance.get(
          `/packing_lists/${selectedListId}`
        );
        if (response.data && response.data.data) {
          const {
            packing_category,
            packing_list_name,
            packing_list_items,
            trip_companion,
          } = response.data.data;
          setSelectedPackingListCategory(packing_category);
          setSelectedPackingListName(packing_list_name); // Set the packing list name
          setTripCompanion(trip_companion);
          const items = packing_list_items.map((item) => ({
            _id: item._id,
            name: item.name,
            completed: item.completed || false, // Use completed from backend
          }));
          setListItems(items);
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to fetch packing list details.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  useEffect(() => {
    fetchPackingListById();
  }, [selectedListId, toast]);

  // Handle checkbox toggle to mark items as completed and update backend
  const handleCheckboxChange = async (itemId) => {
    const updatedItems = listItems.map((item) =>
      item._id === itemId ? { ...item, completed: !item.completed } : item
    );

    setListItems(updatedItems);

    setPackingLists(
      packingLists.map((l) =>
        l._id === selectedListId
          ? { ...l, packing_list_items: updatedItems }
          : l
      )
    );

    // Update the backend
    try {
      await axiosInstance.put(`/packing_lists/${selectedListId}/items`, {
        packing_list_items: updatedItems,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update item status.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle adding a new item to the list
  const handleAddItem = async () => {
    if (!newItemName) return;

    const updatedItems = [
      ...listItems,
      { name: newItemName, completed: false },
    ];

    try {
      const response = await axiosInstance.put(
        `/packing_lists/${selectedListId}/items`,
        { packing_list_items: updatedItems }
      );

      if (response.data.success) {
        setNewItemName(""); // Clear the input field

        const { packing_list_items } = response.data.data;
        setListItems(packing_list_items);

        setPackingLists(
          packingLists.map((l) =>
            l._id === selectedListId
              ? { ...l, packing_list_items: packing_list_items }
              : l
          )
        );
        //TODO: Figure out why it doesn't show new item: (categoryItems[selectedCategory]);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update packing list.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      const response = await axiosInstance.put(
        `/packing_lists/${selectedListId}/items`,
        {
          packing_list_items: listItems.filter((item) => item._id !== itemId),
        }
      );

      if (response.data.success) {
        const { packing_list_items } = response.data.data;
        setListItems(packing_list_items);

        setPackingLists(
          packingLists.map((l) =>
            l._id === selectedListId
              ? { ...l, packing_list_items: packing_list_items }
              : l
          )
        );
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove item.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const calculateProgress = () => {
    if (!listItems || listItems.length === 0) return 0;
    const completedItems = listItems.filter((item) => item.completed).length;
    return (completedItems / listItems.length) * 100;
  };

  const openDeleteDialog = () => setIsDeleteDialogOpen(true);
  const closeDeleteDialog = () => setIsDeleteDialogOpen(false);

  const handleConfirmDelete = () => {
    closeDeleteDialog();
    handleDeletePackingList();
  };

  // Render nothing if no category is selected
  if (!selectedListId) {
    return (
      <Box
        w="30%"
        // p={5}
        // pt={4}
        // ml={0}
        bg="none"
        borderRadius="none"
        boxShadow="none"
      />
    );
  }

  return (
    <Box
      w={{ base: "100%", md: "30%", lg: "30%" }}
      p={5}
      pl={{ base: 0, md: 5 }}
      pt={4}
      ml={0}
      bg={{ base: "none", md: "white" }}
      borderRadius="none"
      boxShadow="none"
      overflowY={{ base: "none", md: "auto", lg: "auto" }}
      sx={{
        "&::-webkit-scrollbar": {
          display: "none",
        },
        msOverflowStyle: "none",
        scrollbarWidth: "none",
      }}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Heading size="md" mb={0}>
          {selectedPackingListCategory || (
            <>
              Packing (Select packing category)
              {/* Only show button if base screen and no category */}
              {isBaseScreenValue && !selectedPackingListCategory && (
                <Box as="span" ml={2}>
                  <IconButton
                    size="sm"
                    colorScheme="green"
                    variant="solid"
                    icon={<AddIcon />}
                    aria-label="Generate AI Packing List"
                    onClick={() => {
                      // TODO: Connect to AI generation logic
                    }}
                  >
                    Generate AI Packing List
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </Heading>
        {selectedPackingListCategory && (
          <ButtonGroup size="sm" spacing={2}>
            <IconButton
              icon={<FaTrash />}
              aria-label="Delete flight"
              borderRadius="full"
              size="xs"
              onClick={openDeleteDialog} // Open warning modal
            />
          </ButtonGroup>
        )}
      </Flex>

      <Text fontSize="sm" color="gray.500" mb={4}>
        {selectedPackingListName || "N/A"}{" "}
        <Text as="span" color="#286328">
          (
          <Translate
            text={
              tripCompanion.charAt(0).toUpperCase() + tripCompanion.slice(1)
            }
          />
          )
        </Text>
      </Text>

      <Flex alignItems="center" mb={4}>
        <Text fontWeight="bold" mr={3} mb={0} color="#286328">
          {calculateProgress().toFixed(0)}%
        </Text>
        <Progress
          value={calculateProgress()}
          size="sm"
          sx={{
            "& > div": {
              backgroundColor: "#286328",
            },
          }}
          flex="1"
          borderRadius="lg"
        />
      </Flex>

      <VStack align="start" spacing={3}>
        {listItems.map((item) => (
          <Flex
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            key={item._id}
            onMouseEnter={() => setHoveredItemKey(item._id)}
            onMouseLeave={() => setHoveredItemKey(null)}
            _hover={{ backgroundColor: "#FAFDFC" }}
            borderRadius="lg"
            cursor="pointer"
            p={1}
          >
            <Checkbox
              isChecked={item.completed}
              onChange={() => handleCheckboxChange(item._id)}
            >
              {/* Render the item name */}
              <Text
                as={item.completed ? "s" : "span"} // Strike-through if completed
                color={item.completed ? "gray.500" : "black"} // Faded color if completed
              >
                {item.name} {/* This is where you render the item name */}
              </Text>
            </Checkbox>

            {hoveredItemKey === item._id && (
              <IconButton
                icon={<FaMinus />}
                colorScheme="red"
                size="xs"
                variant="ghost"
                onClick={() => handleRemoveItem(item._id)}
                aria-label="Remove item"
                _hover={{ color: "red.600" }}
              />
            )}
          </Flex>
        ))}
        <Divider />
        <InputGroup>
          <Input
            placeholder="Add item..."
            variant="filled"
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddItem()}
            bg="none"
            border="1px"
            borderColor="blackAlpha.200"
            borderRadius="xl"
            _hover="none"
          />
          <InputRightElement>
            <IconButton
              onClick={handleAddItem}
              bg="black"
              color="white"
              _hover="none"
              size="sm"
              icon={<AddIcon />}
              aria-label={"Add Item"}
            />
          </InputRightElement>
        </InputGroup>
      </VStack>
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDeleteDialog}
        size={{base: "sm", md: "md"}}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Packing List
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this packing list? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeDeleteDialog}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default PackingListDetails;
